// src/features/records/components/GalleryView/components/GalleryErrorBoundary.js
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { logError, ErrorSeverity, ErrorCategory } from '../../../../../services/errorService';
import mapRecordsErrorToKey from '../../../utils/recordsErrorMapper';
import styles from './GalleryErrorBoundary.module.css';

/**
 * Error boundary component for Gallery View
 * Catches and handles rendering errors in the gallery with retry capability
 */
class GalleryErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('Gallery rendering error:', error, errorInfo);
    
    // Store error info for display
    this.setState({ errorInfo });
    
    // Log to analytics for production monitoring
    logError(error, {
      severity: ErrorSeverity.WARNING,
      category: ErrorCategory.RENDERING,
      component: 'GalleryErrorBoundary',
      action: 'componentDidCatch',
      metadata: {
        componentStack: errorInfo.componentStack,
        retryCount: this.state.retryCount
      }
    });
  }

  handleRetry = () => {
    // Increment retry count
    const newRetryCount = this.state.retryCount + 1;
    
    // Reset error state to retry rendering
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null,
      retryCount: newRetryCount
    });

    // Call refresh callback if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };


  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      const errorKey = mapRecordsErrorToKey(this.state.error, {
        fallbackKey: 'records.views.gallery.errors.boundary_message'
      });

      // Fallback UI when gallery fails to render
      return (
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h3 className={styles.errorTitle}>{t('records.views.gallery.errors.boundary_title')}</h3>
            <p className={styles.errorMessage}>
              {t(errorKey)}
            </p>
            
            <button 
              className={styles.retryButton}
              onClick={this.handleRetry}
              type="button"
            >
              {t('common.retry')}
              {this.state.retryCount > 0 && ` (${this.state.retryCount})`}
            </button>

            {/* Show details in development mode */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className={styles.errorDetails}>
                <summary className={styles.errorSummary}>{t('records.views.gallery.errors.details')}</summary>
                <pre className={styles.errorStack}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    // Render children normally when there's no error
    return this.props.children;
  }
}

GalleryErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  onRetry: PropTypes.func,
  t: PropTypes.func.isRequired
};

export default withTranslation()(GalleryErrorBoundary);
